// components
import PeriodInput from 'components/shared/PeriodInput/PeriodInput';

// hooks
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// types
import type { RefObject } from 'react';
import type { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import type {
  Period,
  PeriodChangeReason
} from 'components/shared/PeriodInput/PeriodInput.types';

type DateTimePeriodSectionProps = {
  timeFieldRef?: RefObject<HTMLInputElement | null>;
};

const DateTimePeriodSection = ({
  timeFieldRef
}: DateTimePeriodSectionProps) => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp.appointmentForm' });

  const {
    setValue,
    control,
    formState: { errors }
  } = useFormContext<AppointmentFormFieldData>();

  const [appointmentStart, appointmentEnd, recurrenceRule] = useWatch({
    name: ['start', 'end', 'recurrencePattern.recurrenceRule'],
    control
  });

  const label = recurrenceRule != 'none' ? t('patternStart') : t('date');
  const period = {
    start: appointmentStart ? new Date(appointmentStart) : new Date(),
    end: appointmentEnd ? new Date(appointmentEnd) : new Date()
  };
  const endTimeError = !!errors ? errors['end']?.message : null;

  const onChange = (period: Period, reason: PeriodChangeReason) => {
    // A correction is PeriodInput reporting the period it already shows -- a stored end before
    // its start gets clamped to a zero duration, for instance. That arrives right after mount,
    // so neither marking the form dirty nor validating it applies: Save stays disabled on a
    // form nobody touched, and the `is-after-start` error the clamped end trips would then sit
    // under a field the user cannot submit anyway. Submitting validates the whole schema, so
    // the error still surfaces the moment the user can act on it.
    const updateOptions = {
      shouldDirty: reason === 'user-edit',
      shouldValidate: reason === 'user-edit'
    };

    setValue('start', period.start.toISOString(), updateOptions);
    setValue('end', period.end.toISOString(), updateOptions);
  };

  return (
    <PeriodInput
      label={label}
      period={period}
      error={endTimeError}
      startTimeInputRef={timeFieldRef}
      onChange={onChange}
    />
  );
};

export default DateTimePeriodSection;
